import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';

// Página 404 → redirección 301 al directorio de navegación.
//
// Cuando un visitante llega a una URL inexistente, registra el 404 (vía la
// función pública logNotFound) indicando el hostname actual (dominio vinculado
// o branding) y redirige permanentemente (history replace) al destino
// devuelto: el destino manual configurado por el admin para esa URL, o
// /directorio (página directorio de menús y categorías) por defecto.
//
// El destino puede ser una ruta interna (/path) o una URL externa (https://):
// las rutas internas se navegan con el router; las externas con
// window.location.replace (redirección cross-domain, útil para dominios de
// marca que apuntan a otra presencia online).
//
// El usuario nunca ve un 404: aterriza en el destino. El admin ve cada URL
// 404 y cada redirección creada en Admin → Redirecciones 404/301.
const EXTERNAL_RE = /^https?:\/\//i;

function go(target, navigate) {
  if (EXTERNAL_RE.test(target)) {
    window.location.replace(target);
  } else {
    navigate(target, { replace: true });
  }
}

export default function PageNotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  const [slow, setSlow] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    const path = location.pathname;
    const referrer = (typeof document !== 'undefined' ? document.referrer : '') || '';
    const hostname = (typeof window !== 'undefined' ? window.location.hostname : '') || '';
    // Fallback: si la función tarda o falla, redirige al directorio a los 1.5s.
    const fallback = setTimeout(() => {
      if (!done.current) {
        done.current = true;
        navigate('/directorio', { replace: true });
      }
    }, 1500);
    const slowTimer = setTimeout(() => setSlow(true), 600);

    (async () => {
      try {
        const res = await base44.functions.invoke('logNotFound', { path, referrer, hostname });
        const target = res?.target || '/directorio';
        if (!done.current) {
          done.current = true;
          clearTimeout(fallback);
          clearTimeout(slowTimer);
          go(target, navigate);
        }
      } catch {
        if (!done.current) {
          done.current = true;
          clearTimeout(fallback);
          clearTimeout(slowTimer);
          navigate('/directorio', { replace: true });
        }
      }
    })();

    return () => { clearTimeout(fallback); clearTimeout(slowTimer); };
  }, [location.pathname, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink">
      <div className="text-center px-6">
        <Loader2 className="w-7 h-7 text-cyan animate-spin mx-auto mb-3" />
        <p className="text-sm text-ink-muted">
          {slow ? 'Redirigiendo al directorio…' : 'Buscando destino…'}
        </p>
      </div>
    </div>
  );
}